import { Globe, ExternalLink, BookOpen } from 'lucide-react';
import { Hero } from '../../components/Hero';

const organizations = [
  {
    name: 'American Academy of Neurology (AAN)',
    description: 'The world\'s largest association of neurologists, providing resources, education, and advocacy for neurological care.',
    website: 'https://www.aan.com',
    category: 'Professional Society',
    logo: 'https://logodix.com/logo/311670.jpg'
  },
  {
    name: 'American Neurological Association (ANA)',
    description: 'Leading professional society for academic neurologists and neuroscientists, advancing research in neurology.',
    website: 'https://myana.org',
    category: 'Professional Society',
    logo: 'https://dovemed-prod-k8s.s3.amazonaws.com/media/images/American_Neurological_Association_ANA.width-750.jpg'
  },
  {
    name: 'International Parkinson and Movement Disorder Society (MDS)',
    description: 'Premier international organization dedicated to advancing the scientific understanding and medical treatment of movement disorders through education, research, and advocacy.',
    website: 'https://www.movementdisorders.org',
    category: 'Specialty Organization',
    logo: 'http://ww1.prweb.com/prfiles/2014/05/06/11831118/mds_rgb_small.jpg'
  },
  {
    name: 'FND Society',
    description: 'International organization dedicated to promoting awareness, research, and education about Functional Neurological Disorder, bringing together clinicians, researchers, and patients.',
    website: 'https://www.fndsociety.org',
    category: 'Specialty Organization',
    logo: 'https://trastornoneurologicofuncional.com/wp-content/uploads/2023/02/fnds-logo.jpg'
  },
  {
    name: 'American Headache Society',
    description: 'Professional society promoting the study and management of headache and face pain.',
    website: 'https://americanheadachesociety.org',
    category: 'Specialty Organization',
    logo: 'https://americanheadachesociety.org/images/American-Headache-Society-Logo.svg'
  },
  {
    name: 'American Autonomic Society',
    description: 'Organization dedicated to the advancement of research and education in autonomic neuroscience.',
    website: 'https://americanautonomicsociety.org',
    category: 'Specialty Organization',
    logo: 'https://americanautonomicsociety.org/wp-content/uploads/2021/02/AAS-Logo.png'
  },
  {
    name: 'The Dysautonomia Project',
    description: 'Organization focused on improving the diagnosis and treatment of autonomic nervous system disorders through physician education, research support, and patient advocacy.',
    website: 'https://thedysautonomiaproject.org',
    category: 'Specialty Organization',
    logo: 'https://thedysautonomiaproject.org/wp-content/uploads/2023/02/TDP-Website-Logo-Blue.webp'
  },
  {
    name: 'American Parkinson Disease Association',
    description: 'The largest grassroots organization dedicated to fighting Parkinson\'s disease through research, education, and support services.',
    website: 'https://www.apdaparkinson.org',
    category: 'Specialty Organization',
    logo: 'https://d2icp22po6iej.cloudfront.net/wp-content/uploads/2017/02/apda-logo-506x130-1.png'
  }
];

export function ProfessionalOrganizations() {
  const categories = Array.from(new Set(organizations.map(org => org.category)));

  return (
    <div>
      <Hero
        title="Professional Organizations"
        description="Explore leading professional organizations and resources in neurology and related fields."
        image="https://images.unsplash.com/photo-1576091160550-2173dba999ef?auto=format&fit=crop&q=80"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-16">
          <Globe className="h-12 w-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Connecting with the Neurological Community
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            We actively participate in and collaborate with leading professional organizations
            to stay at the forefront of neurological care and research.
          </p>
        </div>

        {categories.map((category) => (
          <div key={category} className="mb-20">
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">{category}</h2>
            <div className="grid md:grid-cols-1 lg:grid-cols-2 gap-12">
              {organizations
                .filter(org => org.category === category)
                .map((org, index) => (
                  <div
                    key={index}
                    className="bg-white rounded-xl p-8 shadow-md hover:shadow-xl transition-shadow"
                  >
                    <div className="flex flex-col items-center mb-6">
                      <div className="w-64 h-48 bg-gray-50 rounded-lg flex items-center justify-center p-6 mb-4">
                        <img
                          src={org.logo}
                          alt={`${org.name} logo`}
                          className="max-w-full max-h-full object-contain"
                        />
                      </div>
                      <h3 className="text-2xl font-semibold text-gray-900 text-center">{org.name}</h3>
                    </div>
                    <p className="text-gray-600 text-center mb-6">{org.description}</p>
                    <div className="flex justify-center">
                      <a
                        href={org.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                      >
                        Visit Website
                        <ExternalLink className="h-5 w-5 ml-2" />
                      </a>
                    </div>
                  </div>
                ))}
            </div>
          </div>
        ))}

        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-12 text-center mt-20">
          <BookOpen className="h-16 w-16 text-blue-600 mx-auto mb-6" />
          <h2 className="text-3xl font-bold text-gray-900 mb-4">
            Commitment to Professional Excellence
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Our involvement with these organizations reflects our commitment to providing
            the highest standard of neurological care through continuous learning and
            collaboration with experts worldwide.
          </p>
        </div>
      </div>
    </div>
  );
}